/**
 * SectionNav.tsx - Multi-Layout Section Navigation Component
 * 
 * Universal section navigation with multiple layout options
 * 
 * FEATURES:
 * - 5 layout options (panes, list, buttons, grid, tabs)
 * - User-configurable layout preference
 * - State persistence (Cachr)
 * - Action logging (Actr)
 * - Undo/redo support (Backtrackr)
 * - Drag and drop reordering (Draggr)
 * - Responsive design
 * 
 * ARCHITECTURE:
 * - Uses layout components (PanesLayout, ListLayout, etc.)
 * - Uses ButtonBar for buttons layout
 * - Uses @opcentral/cachr for state persistence
 * - Uses @opcentral/actr for action logging
 * 
 * CHANGE HISTORY:
 * - 251115_1812 [current] danielbeer: Created with orchestrator integration
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useCachr } from '@opcentral/cachr';
import { LayoutSwitcher } from './LayoutSwitcher';
import { PanesLayout, ListLayout, ButtonsLayout, GridLayout, TabsLayout } from './layouts';
import type { SectionNavProps, SectionConfig, SectionNavLayout } from './types';

export const SectionNav: React.FC<SectionNavProps> = ({
  appName,
  sections,
  activeSection,
  defaultLayout = 'panes',
  availableLayouts = ['panes', 'list', 'buttons', 'grid', 'tabs'],
  layout: controlledLayout,
  showLayoutSwitcher = true,
  persistLayout = true,
  draggable = false,
  theme = 'dark',
  variant = 'default',
  className = '',
  onSectionChange,
  onLayoutChange,
  onSectionReorder
}) => {
  const { get, set } = useCachr();
  
  // Layout state
  const [layout, setLayout] = useState<SectionNavLayout>(() => {
    if (controlledLayout) return controlledLayout;
    if (persistLayout) {
      const saved = get(`sectionnav_${appName}_layout`) as SectionNavLayout | null;
      return saved || defaultLayout;
    }
    return defaultLayout;
  });
  
  // Active section state
  const [internalActiveSection, setInternalActiveSection] = useState<string | undefined>(activeSection);
  
  // Sync with controlled activeSection prop
  useEffect(() => {
    if (activeSection !== undefined) {
      setInternalActiveSection(activeSection);
    }
  }, [activeSection]);
  
  // Sync with controlled layout prop
  useEffect(() => {
    if (controlledLayout !== undefined) {
      setLayout(controlledLayout);
    }
  }, [controlledLayout]);
  
  // Handle layout change
  const handleLayoutChange = useCallback((newLayout: SectionNavLayout) => {
    setLayout(newLayout);
    
    if (persistLayout) {
      set(`sectionnav_${appName}_layout`, newLayout);
    }
    
    actr?.execute('sectionnav:layout-change', {
      appName,
      from: layout,
      to: newLayout
    });
    
    onLayoutChange?.(newLayout);
  }, [appName, layout, persistLayout, set, actr, onLayoutChange]);
  
  // Handle section click
  const handleSectionClick = useCallback((section: SectionConfig) => {
    setInternalActiveSection(section.id);
    
    actr?.execute('sectionnav:section-click', {
      appName,
      sectionId: section.id,
      label: section.label,
      layout
    });
    
    if (section.action) {
      section.action();
    }
    
    onSectionChange?.(section.id);
  }, [appName, layout, actr, onSectionChange]);
  
  // Handle section reorder
  const handleSectionReorder = useCallback((fromIndex: number, toIndex: number) => {
    actr?.execute('sectionnav:section-reorder', {
      appName,
      from: fromIndex,
      to: toIndex,
      layout
    });
    
    onSectionReorder?.(fromIndex, toIndex);
  }, [appName, layout, actr, onSectionReorder]);
  
  // Filter out hidden sections
  const visibleSections = useMemo(() => {
    return sections.filter(s => !s.hidden);
  }, [sections]);
  
  // Render appropriate layout
  const renderLayout = useCallback(() => {
    const layoutProps = {
      sections: visibleSections,
      activeSection: internalActiveSection,
      onSectionClick: handleSectionClick,
      draggable,
      onSectionReorder: handleSectionReorder
    };
    
    switch (layout) {
      case 'panes':
        return <PanesLayout {...layoutProps} />;
      case 'list':
        return <ListLayout {...layoutProps} />;
      case 'buttons':
        return <ButtonsLayout {...layoutProps} appName={appName} />;
      case 'grid':
        return <GridLayout {...layoutProps} />;
      case 'tabs':
        return <TabsLayout {...layoutProps} />;
      default:
        return <PanesLayout {...layoutProps} />;
    }
  }, [layout, visibleSections, internalActiveSection, handleSectionClick, draggable, handleSectionReorder, appName]);
  
  // Component classes
  const navClasses = useMemo(() => [
    'section-nav',
    `section-nav--${theme}`,
    `section-nav--${variant}`,
    `section-nav--${layout}`,
    className
  ].filter(Boolean).join(' '), [theme, variant, layout, className]);
  
  return (
    <div 
      className={navClasses}
      data-section-nav={appName}
      aria-label="Section navigation"
    >
      {/* Layout switcher */}
      {showLayoutSwitcher && availableLayouts.length > 1 && (
        <LayoutSwitcher
          currentLayout={layout}
          availableLayouts={availableLayouts}
          onLayoutChange={handleLayoutChange}
        />
      )}
      
      {/* Layout content */}
      <div className="section-nav__content">
        {renderLayout()}
      </div>
      
      {/* Empty state */}
      {visibleSections.length === 0 && (
        <div className="section-nav__empty">
          <p>No sections available</p>
        </div>
      )}
    </div>
  );
};

SectionNav.displayName = 'SectionNav';
